/**
 * CoachPlaybook - PlayerForm Modal Component
 * Version: 0.5.145
 * Description: Mobile-optimized form with configurable optional fields
 * 
 * v0.5.54 Changes:
 * - Jersey templates passed as props from parent (consistent with PlayerCard)
 * - Instant updates when Team Settings changes
 */

import React, { useState, useEffect, useMemo } from 'react';
import { POSITIONS, getPositionsForSport, getPositionMapForSport, isJerseyNumberAvailable } from '../../utils/players';
import { useSport } from '../common/SportContext';
import JerseyIcon, { DEFAULT_JERSEY_TEMPLATE } from '../common/JerseyIcon';
import { getSetting, setSetting } from '../../utils/db';

// Default field visibility settings
const DEFAULT_FIELD_SETTINGS = {
  showHeight: true,
  showGrade: true,
  showGraduationYear: true,
  showNotes: true
};

const ROSTER_FIELDS_KEY = 'rosterFieldSettings';

const PlayerForm = ({ 
  player, 
  players = [],
  jerseyTemplates: propsTemplates = [], 
  activeTemplateId: propsActiveId = 'default',
  onSave, 
  onCancel 
}) => {
  const { sportConfig, currentSport } = useSport();
  const sportPositions = getPositionMapForSport(sportConfig);
  const sportPositionsArray = getPositionsForSport(sportConfig);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    jerseyNumber: '',
    position: '',
    isGoalkeeper: false,
    height: '',
    grade: '',
    graduationYear: '',
    notes: '',
    jerseyTemplateId: null,
    jerseyColor: null,
    shortsColor: null,
    jerseyColorPreset: 'blue',
    shortsColorPreset: 'white',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [teamLogo, setTeamLogo] = useState(null);
  const [jerseyView, setJerseyView] = useState('back');
  const [fieldSettings, setFieldSettings] = useState(DEFAULT_FIELD_SETTINGS);
  const [showFieldSettings, setShowFieldSettings] = useState(false);

  // Use templates from props, fallback to empty array
  const jerseyTemplates = propsTemplates.length > 0 ? propsTemplates : [];
  
  // Get selected template from props templates
  const selectedTemplate = useMemo(() => {
    if (jerseyTemplates.length === 0) return DEFAULT_JERSEY_TEMPLATE;
    
    // If a specific template is selected, use it
    if (selectedTemplateId) {
      const found = jerseyTemplates.find(t => t.id === selectedTemplateId);
      if (found) return found;
    }
    
    // If player has assigned template, use it
    if (player?.jerseyTemplateId) {
      const playerTemplate = jerseyTemplates.find(t => t.id === player.jerseyTemplateId);
      if (playerTemplate) return playerTemplate;
    }
    
    // Prefer default jersey (isDefault), then activeTemplateId, then first
    const defaultJersey = jerseyTemplates.find(t => t.isDefault);
    if (defaultJersey) return defaultJersey;
    
    const active = jerseyTemplates.find(t => t.id === propsActiveId) || jerseyTemplates[0];
    return active || DEFAULT_JERSEY_TEMPLATE;
  }, [jerseyTemplates, selectedTemplateId, player?.jerseyTemplateId, propsActiveId]);

  // Load field settings and team logo (these can stay as IndexedDB loads)
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const logo = await getSetting('teamLogo');
        if (logo) {
          setTeamLogo(logo);
        }
        
        // Load field visibility settings
        const savedFieldSettings = await getSetting(ROSTER_FIELDS_KEY);
        if (savedFieldSettings) {
          setFieldSettings({ ...DEFAULT_FIELD_SETTINGS, ...savedFieldSettings });
        }
        
        // Set initial selected template ID
        if (player?.jerseyTemplateId) {
          setSelectedTemplateId(player.jerseyTemplateId);
        } else if (propsActiveId) {
          setSelectedTemplateId(propsActiveId);
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
      }
    };
    
    loadSettings();
  }, [player]);

  // Populate form if editing
  useEffect(() => {
    if (player) {
      setFormData({
        firstName: player.firstName || '',
        lastName: player.lastName || '',
        jerseyNumber: player.jerseyNumber?.toString() || '',
        position: player.position || '',
        isGoalkeeper: player.isGoalkeeper || player.position === 'GK',
        height: player.height || '',
        grade: player.grade || '',
        graduationYear: player.graduationYear?.toString() || '',
        notes: player.notes || '',
        jerseyTemplateId: player.jerseyTemplateId || null,
        jerseyColor: player.jerseyColor || null,
        shortsColor: player.shortsColor || null,
        jerseyColorPreset: player.jerseyColorPreset || 'blue',
        shortsColorPreset: player.shortsColorPreset || 'white',
      });
    }
  }, [player]);

  const saveFieldSettings = async (newSettings) => {
    setFieldSettings(newSettings);
    await setSetting(ROSTER_FIELDS_KEY, newSettings);
  };

  const toggleField = async (field) => {
    const newSettings = { ...fieldSettings, [field]: !fieldSettings[field] };
    await saveFieldSettings(newSettings);
  };

  const validateForm = async () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.jerseyNumber) {
      newErrors.jerseyNumber = 'Jersey number is required';
    } else {
      const num = parseInt(formData.jerseyNumber);
      if (isNaN(num) || num < 0 || num > 99) {
        newErrors.jerseyNumber = 'Must be between 0-99';
      } else {
        const available = isJerseyNumberAvailable(players, num, player?.id);
        if (!available) {
          newErrors.jerseyNumber = 'Number already taken';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const isValid = await validateForm();
    if (!isValid) return;

    setIsSubmitting(true);

    const playerData = {
      ...formData,
      jerseyNumber: parseInt(formData.jerseyNumber),
      graduationYear: formData.graduationYear ? parseInt(formData.graduationYear) : null,
      jerseyTemplateId: selectedTemplate?.id || null,
      position: formData.isGoalkeeper ? 'GK' : '',
    };

    await onSave(playerData);
    setIsSubmitting(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplateId(template.id);
    setFormData(prev => ({ ...prev, jerseyTemplateId: template.id }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">
              {player ? 'Edit Player' : 'Add New Player'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              * Required fields
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Field Settings Toggle */}
            <button
              type="button"
              onClick={() => setShowFieldSettings(!showFieldSettings)}
              className={`p-2 rounded-lg transition-colors ${showFieldSettings ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:bg-gray-100'}`}
              title="Configure fields"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Field Settings Panel */}
        {showFieldSettings && (
          <div className="px-4 md:px-6 py-3 bg-blue-50 border-b border-blue-100">
            <h3 className="text-sm font-semibold text-blue-800 mb-2">Optional Fields (toggle to show/hide)</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'showHeight', label: 'Height' },
                { key: 'showGrade', label: 'Grade' },
                { key: 'showGraduationYear', label: 'Graduation Year' },
                { key: 'showNotes', label: 'Notes' }
              ].map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleField(key)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    fieldSettings[key]
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-500 border border-gray-300'
                  }`}
                >
                  {fieldSettings[key] ? '✓ ' : ''}{label}
                </button>
              ))}
            </div>
            <p className="text-xs text-blue-600 mt-2">These settings apply to all players and the roster view</p>
          </div>
        )}

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="space-y-5">
            
            {/* Player Sprite Preview — pre-rendered Blender colors */}
            {(() => {
              const COLORS = ['white','black','red','blue','navy','green','yellow','orange','purple','pink','grey','teal'];
              const COLOR_HEX = {
                white:'#f0f0f0', black:'#141414', red:'#d91e1e', blue:'#264adb', navy:'#0d1a59',
                green:'#1a8c33', yellow:'#f2d919', orange:'#f27f19', purple:'#7326b3',
                pink:'#e5669a', grey:'#808080', teal:'#1a99a6'
              };
              const jerseyPreset = formData.jerseyColorPreset || 'blue';
              const shortsPreset = formData.shortsColorPreset || 'white';
              const spriteUrl = `/sprites/color/${jerseyPreset}_${shortsPreset}/player_S.png`;

              return (
                <div className="bg-gray-50 rounded-xl p-4">
                  {/* Sprite preview */}
                  <div className="flex justify-center mb-3">
                    <div style={{ position: 'relative', width: 280, height: 280 }}>
                      <img src={spriteUrl} alt="Player" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      {formData.jerseyNumber && (
                        <div style={{
                          position: 'absolute', top: '42%', left: '50%',
                          transform: 'translate(-50%, -50%)',
                          color: '#fff', fontSize: 32, fontWeight: 800,
                          fontFamily: "'Impact', sans-serif",
                          textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
                        }}>
                          {formData.jerseyNumber}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Jersey color swatches */}
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Jersey</div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {COLORS.map(c => (
                        <div key={c} onClick={() => handleChange('jerseyColorPreset', c)} style={{
                          width: 24, height: 24, borderRadius: 4, cursor: 'pointer',
                          backgroundColor: COLOR_HEX[c],
                          border: jerseyPreset === c ? '3px solid #3b82f6' : '1px solid #d1d5db',
                          boxShadow: jerseyPreset === c ? '0 0 6px rgba(59,130,246,0.5)' : 'none',
                        }} title={c} />
                      ))}
                    </div>
                  </div>

                  {/* Shorts color swatches */}
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Shorts</div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {COLORS.map(c => (
                        <div key={c} onClick={() => handleChange('shortsColorPreset', c)} style={{
                          width: 24, height: 24, borderRadius: 4, cursor: 'pointer',
                          backgroundColor: COLOR_HEX[c],
                          border: shortsPreset === c ? '3px solid #3b82f6' : '1px solid #d1d5db',
                          boxShadow: shortsPreset === c ? '0 0 6px rgba(59,130,246,0.5)' : 'none',
                        }} title={c} />
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}
            
            {/* Required Fields: Name Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">First Name *</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  className={`w-full px-4 py-3 md:py-2 border rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.firstName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter first name"
                />
                {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name *</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  className={`w-full px-4 py-3 md:py-2 border rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.lastName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter last name"
                />
                {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
              </div>
            </div>

            {/* Jersey Number & GK toggle */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Jersey # *</label>
                <input
                  type="number"
                  min="0"
                  max="99"
                  value={formData.jerseyNumber}
                  onChange={(e) => handleChange('jerseyNumber', e.target.value)}
                  className={`w-full px-4 py-3 md:py-2 border rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.jerseyNumber ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="23"
                />
                {errors.jerseyNumber && <p className="mt-1 text-sm text-red-600">{errors.jerseyNumber}</p>}
              </div>
              <div className="flex items-center pt-7">
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={formData.isGoalkeeper}
                    onChange={(e) => handleChange('isGoalkeeper', e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-semibold text-gray-700">Goalkeeper</span>
                </label>
              </div>
            </div>
          </div>
        </form>

        {/* Footer Buttons */}
        <div className="flex gap-3 p-4 md:p-6 border-t bg-gray-50">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : (player ? 'Update Player' : 'Add Player')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlayerForm;
